
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Calendar, Settings, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export function RetentionScheduleManager() {
  const { toast } = useToast();
  const [isTestingSchedule, setIsTestingSchedule] = useState(false);
  const [scheduleConfigOpen, setScheduleConfigOpen] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  
  // Fetch the most recent scheduled executions
  const { data: executionLogs, isLoading, error, refetch } = useQuery({
    queryKey: ['retention-schedule-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('retention_logs')
        .select('*')
        .eq('action', 'scheduled_execution')
        .order('performed_at', { ascending: false })
        .limit(10);
        
      if (error) throw error;
      return data || [];
    },
  });
  
  // Format date to readable string
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Handle test execution of the retention schedule
  const handleTestSchedule = async () => {
    setIsTestingSchedule(true);
    try {
      // Get the current user's token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          variant: "destructive",
          title: "Authentication error",
          description: "You must be logged in to test the schedule",
        });
        return;
      }
      
      // Call the edge function for scheduled retention processing
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-retention-scheduled`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ test: true }),
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const result = await response.json();
      
      toast({
        title: "Schedule test completed",
        description: `Processed ${result.processed} records across ${result.policies_applied} policies`,
      });
      
      // Refresh the logs to show the test execution
      refetch();
      
    } catch (error: any) {
      console.error("Error testing retention schedule:", error);
      toast({
        variant: "destructive",
        title: "Failed to test schedule",
        description: error.message || "Unknown error occurred",
      });
    } finally {
      setIsTestingSchedule(false);
    }
  };
  
  // Handle saving schedule configuration
  const handleSaveScheduleConfig = () => {
    toast({
      title: "Schedule updated",
      description: `Retention will run ${scheduleFrequency} with notifications ${notificationsEnabled ? 'enabled' : 'disabled'}`,
    });
    setScheduleConfigOpen(false);
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Scheduled Retention
          </CardTitle>
          <CardDescription>
            Configure and monitor automatic retention policy execution
          </CardDescription>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={scheduleConfigOpen} onOpenChange={setScheduleConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Settings className="h-4 w-4" />
                Configure
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Schedule Configuration</DialogTitle>
                <DialogDescription>
                  Set how often retention policies should run automatically
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="frequency">Execution Frequency</Label>
                  <Select 
                    value={scheduleFrequency} 
                    onValueChange={setScheduleFrequency}
                  >
                    <SelectTrigger id="frequency" className="w-full">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="notifications">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive emails about execution results
                    </p>
                  </div>
                  <Switch 
                    id="notifications" 
                    checked={notificationsEnabled} 
                    onCheckedChange={setNotificationsEnabled} 
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setScheduleConfigOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveScheduleConfig}>
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Button 
            onClick={handleTestSchedule} 
            disabled={isTestingSchedule}
          >
            {isTestingSchedule ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Clock className="mr-2 h-4 w-4" />
                Run Now
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-6">
          <div className="flex flex-wrap gap-4 md:items-center">
            <div className="bg-muted rounded-md px-3 py-2 text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Frequency: <strong>Weekly</strong></span>
            </div>
            
            <div className="bg-muted rounded-md px-3 py-2 text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Next execution: <strong>May 11, 2025</strong></span>
            </div>
            
            <Badge variant="outline" className="ml-auto">
              Automated
            </Badge>
          </div>
          
          <Tabs defaultValue="history" className="w-full">
            <TabsList>
              <TabsTrigger value="history">Execution History</TabsTrigger>
              <TabsTrigger value="settings">Schedule Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="history" className="space-y-4">
              <div className="border rounded-md divide-y">
                {isLoading ? (
                  <div className="py-10 flex justify-center">
                    <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                  </div>
                ) : error ? (
                  <div className="p-4 text-center">
                    <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-2" />
                    <p>Error loading execution history</p>
                  </div>
                ) : executionLogs && executionLogs.length > 0 ? (
                  executionLogs.map((log: any) => (
                    <div key={log.id} className="p-4">
                      <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {log.details?.success ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                            )}
                            <p className="font-medium">
                              {log.details?.success ? 'Successful Execution' : 'Failed Execution'}
                            </p>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(log.performed_at)}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-1 items-end">
                          <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                            {log.details?.processed || 0} Records Processed
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {log.details?.policies_applied || 0} Policies Applied
                          </span>
                        </div>
                      </div>
                      
                      {log.details?.error && (
                        <div className="mt-2 p-2 bg-destructive/10 text-destructive rounded text-sm">
                          Error: {log.details.error}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-muted-foreground">No execution history found</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2" 
                      onClick={handleTestSchedule}
                    >
                      Run Initial Test
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="settings">
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-2">Current Schedule</h3>
                  <p className="text-sm text-muted-foreground">
                    Retention policies are currently set to run <strong>weekly</strong> on Sunday at 2:00 AM.
                  </p>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h3 className="text-sm font-medium mb-2">Notification Settings</h3>
                  <p className="text-sm text-muted-foreground">
                    Email notifications are <strong>enabled</strong> for execution results.
                  </p>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setScheduleConfigOpen(true)}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Modify Schedule Settings
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
}
